import React, { useEffect, useState } from "react";
import { MultiSelect } from "carbon-components-react";

import { thereWasAProblem } from "@eave/shared/utils/alerts";
import { WaivedEscrow } from "@eave/shared/types/api";

import APIClient from "web_modules/APIClient";

import { ENUM } from "underwriter_dashboard/shared/WorksheetEditor/wsDefs";

const alertWrapper = err => {
  if (err.meta.code === 400) {
    window.alert(`${err.meta.error_detail}`);
  } else {
    thereWasAProblem(err.meta.error_detail);
  }
};

const getWaivedEscrows = async (appId: number): Promise<WaivedEscrow[]> => {
  return await APIClient.get(`/loan_applications/${appId}/hurdles/escrows/waived_escrows`);
};

const addWaivedEscrow = async (appId: number, escrowType: string): Promise<void> => {
  try {
    await APIClient.post(`/loan_applications/${appId}/hurdles/escrows/waived_escrows`, {
      escrow_type: escrowType,
    });
  } catch (err) {
    alertWrapper(err);
  }
};

const deleteWaivedEscrow = async (appId: number, escrowType: string): Promise<boolean> => {
  return await APIClient.deleteRequest(
    `/loan_applications/${appId}/hurdles/escrows/waived_escrows/${escrowType}`,
  );
};

const waivedEscrowsToEscrowTypeStrings = (waivedEscrows: WaivedEscrow[]) => {
  return waivedEscrows.map((waivedEscrow: WaivedEscrow) => waivedEscrow.escrow_type);
};

const WaivedEscrows = ({ appId }) => {
  const [waivedEscrows, setWaivedEscrows] = useState<null | string[]>(null);

  const fetchData = async () => {
    getWaivedEscrows(appId).then((waivedEscrows: WaivedEscrow[]) => {
      setWaivedEscrows(null);
      setWaivedEscrows(waivedEscrowsToEscrowTypeStrings(waivedEscrows));
    });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleChanges = async (selectedItems: string[]) => {
    const itemsToDelete = waivedEscrows.filter(
      (waivedEscrow: string) => waivedEscrow && !selectedItems.includes(waivedEscrow),
    );
    const itemsToAdd = selectedItems.filter(
      (selectedEscrow: string) => selectedEscrow && !waivedEscrows.includes(selectedEscrow),
    );

    for (const item of itemsToDelete) {
      await deleteWaivedEscrow(appId, item);
    }

    for (const item of itemsToAdd) {
      await addWaivedEscrow(appId, item);
    }

    await fetchData();
  };

  return (
    <div className="waived-escrows">
      {!waivedEscrows && <div>Loading!</div>}
      {waivedEscrows && (
        <MultiSelect
          id="inline"
          titleText="Waived escrows"
          label="Exclude additional escrows"
          type="inline"
          items={ENUM.ESCROW_ITEM}
          itemToString={item => item}
          initialSelectedItems={ENUM.ESCROW_ITEM.filter((escrowItem: string) =>
            waivedEscrows.includes(escrowItem),
          )}
          onChange={e => {
            handleChanges(e.selectedItems);
          }}
        />
      )}
      <p style={{ marginTop: "1rem", marginBottom: "2rem" }}>
        Escrow types added above will not be automatically populated/re-populated going forward.
        <br />
        If escrow below related to a waived escrow type already exists, then you must do a one time
        manual removal!
      </p>
    </div>
  );
};

export default WaivedEscrows;
